<script lang="ts">
  import InlineSVG from 'svelte-inline-svg';

  import type {DateRange} from '../../types/date';

  import handleClickOutside from '../../utils/handle-click-outside';
  import {getLocaleDateFormatPattern} from '../../utils/date-utils';

  import clearIcon from '../../assets/icons/cross.svg';

  import IconButton from '../icon-button/IconButton.svelte';
  import Calendar from '../calendar/Calendar.svelte';

  export let type: 'date' | 'month' = 'date';
  export let label: string | null = null;
  export let placeholder =
    (type === 'date' && getLocaleDateFormatPattern()) ||
    (type === 'month' &&
      getLocaleDateFormatPattern(undefined, {
        month: 'long',
        year: 'numeric'
      })) ||
    '';
  export let value: DateRange = {start: null, end: null};
  export let min: Date | null = null;
  export let max: Date | null = null;
  export let id: string | null = null;
  export let required = {start: false, end: false};
  export let closeOnSelect = true;

  let showStartDatePicker = false;
  let showEndDatePicker = false;

  $: formattedValue = (() => {
    let formattedStart = '';
    let formattedEnd = '';

    if (value.start) {
      if (type === 'date') {
        formattedStart = value.start.toLocaleDateString();
      } else if (type === 'month') {
        formattedStart = value.start.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric'
        });
      }
    }

    if (value.end) {
      if (type === 'date') {
        formattedEnd = value.end.toLocaleDateString();
      } else if (type === 'month') {
        formattedEnd = value.end.toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric'
        });
      }
    }

    return {start: formattedStart, end: formattedEnd};
  })();

  // Reset the input value and close the date picker
  const clearInput = (property: keyof DateRange) => {
    // Timeout needed to not remove the target element before
    // event listeners could work with this target element.
    // Removing this timeout will for example cause that
    // the filters dropdown is closed immediately.
    setTimeout(() => {
      if (property === 'start') {
        showStartDatePicker = false;
      } else if (property === 'end') {
        showEndDatePicker = false;
      }
      value[property] = null;
    });
  };
</script>

<style lang="postcss">
  .container {
    display: grid;
    gap: 0.25rem;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 768px) {
    .container {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1.5rem;
    }

    .label {
      display: flex;
      align-items: center;
      justify-content: end;
      min-height: var(--ubilabs-input-height);
    }
  }

  .inputs {
    display: grid;
    gap: 0.5rem 1rem;
  }

  @media (min-width: 768px) {
    .inputs {
      grid-column: 2;
      grid-template-columns: 1fr 1fr;
    }
  }

  .inputGroup {
    position: relative;
    display: grid;
    grid-template-columns: 2.5rem 1fr;
    gap: 0.25rem;
    align-items: center;
  }

  @media (min-width: 768px) {
    .inputGroup {
      grid-template-columns: auto 1fr;
    }
  }

  .inputGroup > div {
    position: relative;
  }

  .inputGroup input {
    width: 100%;
    caret-color: transparent;
  }

  .inputGroup input + :global(.inputClearButton) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    margin: 0.25rem;
  }

  :global(.dateRangePickerCalendar) {
    z-index: 10;
    position: absolute;
    top: 100%;
    left: 0;
  }
</style>

<div class="container {$$props.class}">
  {#if label}
    <div class="label">
      <label for={id}>{label}</label>
    </div>
  {/if}
  <div class="inputs {$$props.inputWrapperClass}">
    <div
      class="inputGroup"
      use:handleClickOutside
      on:click_outside={() => {
        showStartDatePicker = false;
      }}>
      <label for={`${id}-start`}>from{required.start ? '*' : ''}</label>

      <div>
        <!-- prevent default on input keydown and hide caret to simulate a readonly field without losing the browser's native form validation -->
        <input
          type="text"
          autocomplete="off"
          required={required.start}
          {placeholder}
          name={`${id}-start`}
          id={`${id}-start`}
          value={formattedValue.start}
          on:focus|preventDefault={() => (showStartDatePicker = true)}
          on:keydown|preventDefault />

        {#if formattedValue.start}
          <IconButton
            class="inputClearButton"
            on:click={() => clearInput('start')}>
            <InlineSVG src={clearIcon} />
          </IconButton>
        {/if}

        {#if showStartDatePicker}
          <Calendar
            class="dateRangePickerCalendar"
            {type}
            selectedDate={value.start}
            minDate={min}
            maxDate={value.end || max}
            on:change={({detail: newDate}) => {
              if (closeOnSelect) {
                showStartDatePicker = false;
              }
              value.start = newDate;
            }} />
        {/if}
      </div>
    </div>

    <div
      class="inputGroup"
      use:handleClickOutside
      on:click_outside={() => {
        showEndDatePicker = false;
      }}>
      <label for={`${id}-end`}>to{required.end ? '*' : ''}</label>

      <div>
        <!-- prevent default on input keydown and hide caret to simulate a readonly field without losing the browser's native form validation -->
        <input
          type="text"
          autocomplete="off"
          required={required.end}
          {placeholder}
          name={`${id}-end`}
          id={`${id}-end`}
          value={formattedValue.end}
          on:focus|preventDefault={() => (showEndDatePicker = true)}
          on:keydown|preventDefault />

        {#if formattedValue.end}
          <IconButton
            class="inputClearButton"
            on:click={() => clearInput('end')}>
            <InlineSVG src={clearIcon} />
          </IconButton>
        {/if}

        {#if showEndDatePicker}
          <Calendar
            class="dateRangePickerCalendar"
            {type}
            selectedDate={value.end}
            minDate={value.start || min}
            maxDate={max}
            on:change={({detail: newDate}) => {
              if (closeOnSelect) {
                showEndDatePicker = false;
              }
              value.end = newDate;
            }} />
        {/if}
      </div>
    </div>
  </div>
</div>
